import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

interface NavigationWrapperProps {
  children: React.ReactNode;
}

const NavigationWrapper: React.FC<NavigationWrapperProps> = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const prevPathnameRef = useRef(location.pathname);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Intercept all link clicks (including React Router Links)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href) {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);
        
        // Only show loading for internal navigation when pathname changes
        if (url.origin === currentUrl.origin) {
          // Only show loading if pathname is different (ignore query param changes)
          if (url.pathname !== currentUrl.pathname) {
            setIsLoading(true);
          }
        }
      }
    };

    // Use capture phase to catch clicks early
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  useEffect(() => {
    // Only show loading when pathname changes, not when only search params change
    if (location.pathname !== prevPathnameRef.current) {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      // Show loading if not already showing
      setIsLoading(true);
      
      // Update previous pathname
      prevPathnameRef.current = location.pathname;
      
      // Hide loading after route renders
      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
      }, 300);

      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      };
    } else {
      // Same pathname (e.g. effect re-run in Strict Mode, or hash/search-only change):
      // ensure spinner is hidden so we don't get stuck in loading state
      setIsLoading(false);
    }
  }, [location.pathname]);

  return (
    <>
      <LoadingSpinner isVisible={isLoading} />
      {children}
    </>
  );
};

export default NavigationWrapper;

